<script setup lang="ts">
import { onMounted, ref } from 'vue';

import { api } from '../../api/client.js';
import { uuid } from '../../api/documents.js';

/**
 * Processamento de posições — somente na área administrativa (M03).
 *
 * Mostra jobs, execuções, erros seguros e a avaliação SIMULADA (métricas contra
 * a verdade do simulador, calculadas por outro componente). A única ação é
 * enfileirar reprocessamento idempotente: nada aqui edita estimativa ou marca
 * job como concluído.
 */
interface Job {
  jobId: string; sessionId: string; triggerKind: string; state: string; attempts: number;
  createdAtMs: number; finishedAtMs: number | null; errorCode: string | null; errorDetail: string | null;
}
interface Run {
  runId: string; sessionId: string; state: string; algorithmVersion: string; configRevision: string;
  inputRevision: string | null; durationMs: number | null; captureToPublishMs: number | null;
  outputs: { total: number; withPosition: number; withoutPosition: number; invalidated: number };
  errorCode: string | null; errorDetail: string | null;
  evaluation: {
    comparableCount: number; unmatchedCount: number; meanErrorMeters: number | null;
    medianErrorMeters: number | null; p95ErrorMeters: number | null; maxErrorMeters: number | null; method: string;
  } | null;
}
interface Overview {
  counts: Record<string, number>; oldestPendingAgeMs: number | null; jobs: Job[]; runs: Run[];
}

const props = defineProps<{ tenantId: string }>();
const overview = ref<Overview | null>(null);
const message = ref<string | null>(null);
const busy = ref(false);
/** Um requestId por sessão nesta tela: clique repetido é o MESMO pedido. */
const requestIds = new Map<string, string>();

async function refresh(): Promise<void> {
  const result = await api.processingOverview<Overview>(props.tenantId);
  if (result.status === 200) overview.value = result.body;
  else message.value = `Falha ao consultar processamento (HTTP ${result.status}).`;
}

async function reprocess(sessionId: string): Promise<void> {
  busy.value = true;
  try {
    const requestId = requestIds.get(sessionId) ?? uuid();
    requestIds.set(sessionId, requestId);
    const result = await api.reprocess<{ jobId: string; state: string; duplicate: boolean }>({
      schemaVersion: 'rfid.mvp.processing-reprocess/1.0.0',
      requestId,
      tenantId: props.tenantId,
      sessionId,
    });
    message.value = result.status === 202
      ? `Reprocessamento ${result.body.duplicate ? 'já enfileirado' : 'enfileirado'} (${result.body.state}); o worker publica quando concluir.`
      : `Reprocessamento recusado (HTTP ${result.status}).`;
    await refresh();
  } finally {
    busy.value = false;
  }
}

function meters(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)} m`;
}

onMounted(refresh);
defineExpose({ refresh, reprocess });
</script>

<template>
  <section class="processing">
    <h3>Processamento de posições</h3>
    <button
      type="button"
      @click="refresh"
    >
      Atualizar processamento
    </button>
    <p
      v-if="message !== null"
      role="status"
    >
      {{ message }}
    </p>
    <template v-if="overview !== null">
      <p>
        Pendentes {{ overview.counts.pending }} · em execução {{ overview.counts.running }} ·
        concluídos {{ overview.counts.succeeded }} · falhos {{ overview.counts.failed }} ·
        absorvidos {{ overview.counts.superseded }}
        <span v-if="overview.oldestPendingAgeMs !== null">
          · pendente mais antigo há {{ Math.round(overview.oldestPendingAgeMs / 1000) }} s
        </span>
      </p>
      <table>
        <caption>Execuções</caption>
        <thead>
          <tr>
            <th>Sessão</th><th>Estado</th><th>Algoritmo</th><th>Revisão de entrada</th>
            <th>Duração</th><th>Captura→publicação</th><th>Com/sem posição</th><th>Erro</th>
            <th>Avaliação simulada</th><th />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="run in overview.runs"
            :key="run.runId"
          >
            <td><code>{{ run.sessionId.slice(0, 8) }}</code></td>
            <td>{{ run.state }}</td>
            <td>{{ run.algorithmVersion }} / {{ run.configRevision }}</td>
            <td><code>{{ run.inputRevision?.slice(0, 19) ?? '—' }}</code></td>
            <td>{{ run.durationMs === null ? '—' : `${run.durationMs} ms` }}</td>
            <td>{{ run.captureToPublishMs === null ? '—' : `${Math.round(run.captureToPublishMs / 1000)} s` }}</td>
            <td>{{ run.outputs.withPosition }} / {{ run.outputs.withoutPosition }}</td>
            <td>{{ run.errorCode === null ? '—' : `${run.errorCode}: ${run.errorDetail}` }}</td>
            <td>
              <span v-if="run.evaluation === null">—</span>
              <span v-else-if="run.evaluation.comparableCount === 0">
                avaliação simulada sem correspondência ({{ run.evaluation.unmatchedCount }} sem par)
              </span>
              <span v-else>
                avaliação simulada: {{ run.evaluation.comparableCount }} pares, médio
                {{ meters(run.evaluation.meanErrorMeters) }}, mediano {{ meters(run.evaluation.medianErrorMeters) }},
                p95 {{ meters(run.evaluation.p95ErrorMeters) }}, máx {{ meters(run.evaluation.maxErrorMeters) }}
              </span>
            </td>
            <td>
              <button
                type="button"
                :disabled="busy"
                @click="reprocess(run.sessionId)"
              >
                Reprocessar sessão
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <details>
        <summary>Jobs ({{ overview.jobs.length }})</summary>
        <ul>
          <li
            v-for="job in overview.jobs"
            :key="job.jobId"
          >
            <code>{{ job.sessionId.slice(0, 8) }}</code> {{ job.triggerKind }} — {{ job.state }}
            (tentativas {{ job.attempts }})
            <span v-if="job.errorCode !== null"> — {{ job.errorCode }}: {{ job.errorDetail }}</span>
          </li>
        </ul>
      </details>
    </template>
  </section>
</template>
